import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { RefineModelModal } from '../components/RefineModelModal';
import { MobileNav } from '../components/MobileNav';
// Derive system model from answers
const deriveSystemModel = (answers: string[]) => {
  const systemPatterns = {
    oscillation: {
      corePattern: `Your work system tends to move between opposing forces rather than settling into one stable mode. When faced with decisions, you often feel the pull of multiple valid directions. This isn't indecision — it's your system staying responsive to different kinds of value. You resist locking in prematurely, but you also resist staying in motion indefinitely.`,
      tension: 'Freedom and Structure',
      tensionDescription: "You want open space to explore, but you also want clear boundaries to work within. These aren't opposites, you need to resolve ; they're poles your system moves between.",
      underPressure: ['You cycle between tightening control and loosening it', 'Decisions get delayed while you hold multiple options', 'Commitment becomes harder as stakes increase'],
      currentMode: 'Oscillation',
      modeDescription: 'More than one direction is active. No single path has taken priority yet.'
    },
    convergence: {
      corePattern: `Your work system tends to narrow and lock in. When faced with ambiguity, you move toward structure and definition. You prefer committed direction over open exploration, and you're more comfortable executing a plan than holding multiple possibilities. You resist staying in undefined states, but you also resist reopening decisions once they're made.`,
      tension: 'Commitment and Flexibility',
      tensionDescription: "You want to lock in and execute, but you also sense when rigidity becomes a problem. These aren't opposites, you need to resolve ; they're poles your system moves between.",
      underPressure: ['You commit earlier and push harder', 'Reopening decisions feels costly', 'Flexibility becomes harder to access'],
      currentMode: 'Stabilization',
      modeDescription: "The system has settled into a defined direction. It's optimizing for execution over exploration."
    },
    divergence: {
      corePattern: `Your work system tends to expand and explore. When faced with constraints, you look for ways to open up the space. You prefer keeping options available over locking into one path, and you're more comfortable in motion than in execution mode. You resist premature commitment, but you also resist the feeling of never landing anywhere.`,
      tension: 'Exploration and Execution',
      tensionDescription: "You want to keep exploring possibilities, but you also need to land and deliver. These aren't opposites, you need to resolve ; they're poles your system moves between.",
      underPressure: ['You generate more options instead of narrowing', 'Execution gets delayed while you explore', 'Closing becomes harder as stakes increase'],
      currentMode: 'Transition',
      modeDescription: "The system is moving between states. It hasn't settled into a stable operating mode yet."
    }
  };
  const aCount = answers.filter(a => a.includes('Narrow') || a.includes('Push') || a.includes('undefined')).length;
  const bCount = answers.filter(a => a.includes('open') || a.includes('Step back') || a.includes('locked')).length;
  if (Math.abs(aCount - bCount) <= 2) {
    return systemPatterns.oscillation;
  } else if (aCount > bCount) {
    return systemPatterns.convergence;
  } else {
    return systemPatterns.divergence;
  }
};
export function WorkMirrorResult() {
  const navigate = useNavigate();
  const [systemModel, setSystemModel] = useState<any>(null);
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [anchorsAdded, setAnchorsAdded] = useState(false);
  useEffect(() => {
    const answersJson = sessionStorage.getItem('workMirrorAnswers');
    if (!answersJson) {
      navigate('/mirrors/work');
      return;
    }
    const answers = JSON.parse(answersJson);
    const model = deriveSystemModel(answers);
    setSystemModel(model);
    const anchorsAddedFlag = sessionStorage.getItem('workMirrorAnchorsAdded');
    if (anchorsAddedFlag === 'true') {
      setAnchorsAdded(true);
    }
  }, [navigate]);
  const handleRefineSubmit = (data: {
    dateOfBirth: string;
    timeOfBirth: string;
    placeOfBirth: string;
  }) => {
    sessionStorage.setItem('workMirrorAnchors', JSON.stringify(data));
    sessionStorage.setItem('workMirrorAnchorsAdded', 'true');
    setIsRefineModalOpen(false);
    setIsRefining(true);
    setTimeout(() => {
      setIsRefining(false);
      setAnchorsAdded(true);
    }, 2000);
  };
  if (!systemModel) {
    return null;
  }
  if (isRefining) {
    return <>
        <Helmet data-id="element-5035">
          <title data-id="element-5036">Refining... | Lazy Monk Labs</title>
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-4 sm:px-6" data-id="element-5037">
          <div className="max-w-2xl w-full text-center space-y-8" data-id="element-5038">
            <h1 className="text-4xl sm:text-5xl text-[#F5F5F5]" data-id="element-5039">Refining…</h1>
            <p className="text-lg text-gray-400" data-id="element-5040">
              Recomputing with structural anchors.
            </p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-5041">
        <title data-id="element-5042">Your Work System | Lazy Monk Labs</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-5043">
        <MobileNav data-id="element-5044" />

        {/* RESULTS PAGE: System state snapshot with breathing room */}
        <main className="pt-16 sm:pt-20 pb-12 sm:pb-16" data-id="element-5045">
          <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-5046">
            {/* Status header - matches App Home */}
            <div className="py-5 sm:py-6 border-b border-[#2B2B2B] flex items-center justify-between" data-id="element-5047">
              <div data-id="element-5048">
                <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-5049">
                  Work Mirror
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-5050">
                  Initial • Low confidence
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-5051">
                <span className="text-xs px-2 py-1 border border-[#2B2B2B] text-gray-500" data-id="element-5052">
                  {systemModel.currentMode}
                </span>
              </div>
            </div>

            {/* Main content - stacked blocks with breathing room */}
            <div className="space-y-6 mt-8 sm:mt-10" data-id="element-5053">
              {/* CORE PATTERN */}
              <div className="border border-[#2B2B2B] bg-[#0A0A0A]" data-id="element-5054">
                <div className="p-5 sm:p-6 border-b border-[#2B2B2B]" data-id="element-5055">
                  <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-5056">
                    Core Pattern
                  </p>
                </div>
                <div className="p-5 sm:p-6 space-y-4" data-id="element-5057">
                  {systemModel.corePattern.split('. ').filter((s: string) => s.trim()).map((sentence: string, index: number) => <p key={index} className="text-sm text-[#F5F5F5] leading-relaxed" data-id="element-5058">
                        {sentence.trim()}
                        {sentence.endsWith('.') ? '' : '.'}
                      </p>)}
                </div>
              </div>

              {/* ACTIVE TENSION */}
              <div className="border border-[#2B2B2B] bg-[#0A0A0A]" data-id="element-5059">
                <div className="p-5 sm:p-6 border-b border-[#2B2B2B]" data-id="element-5060">
                  <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-5061">
                    Active Tension
                  </p>
                </div>
                <div className="p-5 sm:p-6 space-y-3" data-id="element-5062">
                  <p className="text-base font-medium text-[#F5F5F5]" data-id="element-5063">
                    {systemModel.tension}
                  </p>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-5064">
                    {systemModel.tensionDescription}
                  </p>
                </div>
              </div>

              {/* WHEN PRESSURE INCREASES */}
              <div className="border border-[#2B2B2B] bg-[#0A0A0A]" data-id="element-5065">
                <div className="p-5 sm:p-6 border-b border-[#2B2B2B]" data-id="element-5066">
                  <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-5067">
                    When Pressure Increases
                  </p>
                </div>
                <div className="p-5 sm:p-6 space-y-3" data-id="element-5068">
                  {systemModel.underPressure.map((item: string, index: number) => <p key={index} className="text-sm text-gray-400 leading-relaxed" data-id="element-5069">
                        {item}
                      </p>)}
                </div>
              </div>

              {/* CURRENT STATE */}
              <div className="border border-[#2B2B2B] bg-[#111214]" data-id="element-5070">
                <div className="p-5 sm:p-6 border-b border-[#2B2B2B]" data-id="element-5071">
                  <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-5072">
                    Current State
                  </p>
                </div>
                <div className="p-5 sm:p-6 space-y-3" data-id="element-5073">
                  <p className="text-base font-medium text-[#F5F5F5]" data-id="element-5074">
                    {systemModel.currentMode}
                  </p>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-5075">
                    {systemModel.modeDescription}
                  </p>
                </div>
              </div>
            </div>

            {/* Continuity footer */}
            <div className="mt-10 sm:mt-12 pt-8 sm:pt-10 border-t border-[#2B2B2B] space-y-3" data-id="element-5076">
              <p className="text-xs text-gray-600" data-id="element-5077">Model confidence: Low</p>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-5078">
                The mirror sharpens as you log real situations.
              </p>
            </div>

            {/* Actions */}
            <div className="mt-8 sm:mt-10 space-y-4" data-id="element-5079">
              <Link to="/mirrors/work/app" className="block w-full p-4 border border-[#F5F5F5] bg-[#F5F5F5] text-[#0A0A0A] text-center text-xs font-medium uppercase tracking-wider hover:bg-gray-200 transition-all" data-id="element-5080">
                Continue to Work Mirror
              </Link>

              {!anchorsAdded && <button onClick={() => setIsRefineModalOpen(true)} className="block w-full p-4 border border-[#2B2B2B] text-center text-xs font-medium text-gray-500 hover:text-[#F5F5F5] hover:border-[#F5F5F5] transition-all" data-id="element-5081">
                  Refine Precision
                </button>}
            </div>
          </div>
        </main>

        <RefineModelModal isOpen={isRefineModalOpen} onClose={() => setIsRefineModalOpen(false)} onSubmit={handleRefineSubmit} data-id="element-5082" />
      </div>
    </>;
}